import {
    type ReactNode,
    useState
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    ColorSlider,
    ColorArea,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    DocsTable,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "ColorArea";

const USAGE_CODE = `const [hue, setHue] = useState(181);

<ColorSlider
    onChange={setHue}
    color="#006155"
    channel="hue"
    value={hue}
/>

<ColorArea
    onChange={({ lightness, chroma }) => save(lightness, chroma)}
    hue={hue}
/>`;

const GUIDE_CODE = `<ColorArea
    contrastSurface="#ffffff"
    contrastGuides={[4.5, 3]}
    hue={181}
/>`;

const SHAPE_CODE = `<ColorArea
    aspectRatio={2.4}
    isFlush={true}
    hue={181}
/>`;

const TEAL_HUE = 181;

const BLUE_HUE = 264;

const INITIAL_VALUE = {
    lightness: 0.44,
    chroma: 0.08
};

const stylesheet = StyleSheet.create({
    block: {
        alignSelf: "stretch"
    }
});

const Figure = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.block,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {label}
        </Text>
        {children}
    </View>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("colorAreaPage.whyTitle"),
        id: "why"
    },
    {
        title: t("colorAreaPage.gamutTitle"),
        id: "gamut"
    },
    {
        title: t("colorAreaPage.guideTitle"),
        id: "guide"
    },
    {
        title: t("colorAreaPage.shapeTitle"),
        id: "shape"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const OverviewPreview = () => {
    const {
        t
    } = useDocsI18n();

    const [
        hue,
        setHue
    ] = useState<number>(TEAL_HUE);

    return <Preview
        caption={t("colorAreaPage.overviewCaption")}
        isStacked={true}
    >
        <ColorArea
            initialValue={INITIAL_VALUE}
            hue={hue}
        />
        <Figure
            label={t("colorAreaPage.hueLabel")}
        >
            <ColorSlider
                onChange={setHue}
                color="#006155"
                channel="hue"
                value={hue}
            />
        </Figure>
    </Preview>;
};

const ColorAreaPage = () => {
    const {
        t
    } = useDocsI18n();

    return <Article>
        <ComponentHeader
            lead={t("colorAreaPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("colorAreaPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("colorAreaPage.overviewNote")}
            </Paragraph>
            <OverviewPreview />
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("colorAreaPage.whyTitle")}
            id="why"
        >
            <Paragraph>
                {t("colorAreaPage.whyText")}
            </Paragraph>
            <Paragraph>
                {t("colorAreaPage.whyContrast")}
            </Paragraph>
            <Preview
                caption={t("colorAreaPage.whyCaption")}
                isStacked={true}
            >
                <Figure
                    label={`hue={${TEAL_HUE}}`}
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        hue={TEAL_HUE}
                    />
                </Figure>
                <Figure
                    label={`hue={${BLUE_HUE}}`}
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        hue={BLUE_HUE}
                    />
                </Figure>
            </Preview>
            <Callout>
                {t("colorAreaPage.whyAxis")}
            </Callout>
        </Section>
        <Section
            title={t("colorAreaPage.gamutTitle")}
            id="gamut"
        >
            <Paragraph>
                {t("colorAreaPage.gamutText")}
            </Paragraph>
            <Preview
                caption={t("colorAreaPage.gamutCaption")}
                isStacked={true}
            >
                <Figure
                    label={"shape=\"square\""}
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        hue={BLUE_HUE}
                    />
                </Figure>
                <Figure
                    label={"shape=\"gamut\""}
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        hue={BLUE_HUE}
                        shape="gamut"
                    />
                </Figure>
            </Preview>
        </Section>
        <Section
            title={t("colorAreaPage.guideTitle")}
            id="guide"
        >
            <Paragraph>
                {t("colorAreaPage.guideText")}
            </Paragraph>
            <Paragraph>
                {t("colorAreaPage.guideAlpha")}
            </Paragraph>
            <Preview
                caption={t("colorAreaPage.guideCaption")}
                isStacked={true}
            >
                <Figure
                    label="contrastGuides={[]}"
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        contrastGuides={[]}
                        hue={TEAL_HUE}
                    />
                </Figure>
                <Figure
                    label="contrastGuides={[4.5]}"
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        contrastGuides={[
                            4.5
                        ]}
                        hue={TEAL_HUE}
                    />
                </Figure>
                <Figure
                    label="contrastGuides={[4.5, 3]}"
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        contrastGuides={[
                            4.5,
                            3
                        ]}
                        hue={TEAL_HUE}
                    />
                </Figure>
            </Preview>
            <CodeBlock
                code={GUIDE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("colorAreaPage.shapeTitle")}
            id="shape"
        >
            <Paragraph>
                {t("colorAreaPage.shapeText")}
            </Paragraph>
            <Preview
                caption={t("colorAreaPage.shapeCaption")}
                isStacked={true}
            >
                <Figure
                    label="aspectRatio={2.4}"
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        aspectRatio={2.4}
                        hue={TEAL_HUE}
                    />
                </Figure>
                <Figure
                    label="isFlush={true}"
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        aspectRatio={2.4}
                        hue={TEAL_HUE}
                        isFlush={true}
                    />
                </Figure>
                <Figure
                    label="isDisabled={true}"
                >
                    <ColorArea
                        initialValue={INITIAL_VALUE}
                        aspectRatio={2.4}
                        isDisabled={true}
                        hue={TEAL_HUE}
                    />
                </Figure>
            </Preview>
            <CodeBlock
                code={SHAPE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("colorAreaPage.a11yRole"),
                    t("colorAreaPage.a11yValue"),
                    t("colorAreaPage.a11yNative"),
                    t("colorAreaPage.a11yContrast"),
                    t("colorAreaPage.a11yTouch")
                ]}
            />
            <DocsTable
                rows={[
                    [
                        <InlineCode
                            key="leftRight"
                        >
                            {t("colorAreaPage.keyLeftRight")}
                        </InlineCode>,
                        t("colorAreaPage.keyLeftRightAction")
                    ],
                    [
                        <InlineCode
                            key="upDown"
                        >
                            {t("colorAreaPage.keyUpDown")}
                        </InlineCode>,
                        t("colorAreaPage.keyUpDownAction")
                    ],
                    [
                        <InlineCode
                            key="page"
                        >
                            {t("colorAreaPage.keyPage")}
                        </InlineCode>,
                        t("colorAreaPage.keyPageAction")
                    ],
                    [
                        <InlineCode
                            key="home"
                        >
                            {t("colorAreaPage.keyHome")}
                        </InlineCode>,
                        t("colorAreaPage.keyHomeAction")
                    ],
                    [
                        <InlineCode
                            key="end"
                        >
                            {t("colorAreaPage.keyEnd")}
                        </InlineCode>,
                        t("colorAreaPage.keyEndAction")
                    ]
                ]}
                headers={[
                    t("colorAreaPage.keyColumn"),
                    t("colorAreaPage.actionColumn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("colorAreaPage.propHue"),
                        type: "number",
                        name: "hue"
                    },
                    {
                        type: "{ lightness: number; chroma: number }",
                        description: t("colorAreaPage.propValue"),
                        name: "value"
                    },
                    {
                        description: t("colorAreaPage.propInitialValue"),
                        defaultValue: "{ lightness: 0.5, chroma: 0 }",
                        type: "{ lightness: number; chroma: number }",
                        name: "initialValue"
                    },
                    {
                        type: "(value: { lightness: number; chroma: number }) => void",
                        description: t("colorAreaPage.propOnChange"),
                        name: "onChange"
                    },
                    {
                        type: "(value: { lightness: number; chroma: number }) => void",
                        description: t("colorAreaPage.propOnChangeEnd"),
                        name: "onChangeEnd"
                    },
                    {
                        description: t("colorAreaPage.propAlpha"),
                        defaultValue: "1",
                        type: "number",
                        name: "alpha"
                    },
                    {
                        description: t("colorAreaPage.propContrastGuides"),
                        name: "contrastGuides",
                        type: "Array<number>",
                        defaultValue: "[4.5]"
                    },
                    {
                        description: t("colorAreaPage.propContrastSurface"),
                        name: "contrastSurface",
                        type: "string"
                    },
                    {
                        description: t("colorAreaPage.propShape"),
                        type: "\"square\" | \"gamut\"",
                        defaultValue: "\"square\"",
                        name: "shape"
                    },
                    {
                        description: t("colorAreaPage.propIsGamutShown"),
                        defaultValue: "true",
                        name: "isGamutShown",
                        type: "boolean"
                    },
                    {
                        description: t("colorAreaPage.propAspectRatio"),
                        defaultValue: "1.6",
                        name: "aspectRatio",
                        type: "number"
                    },
                    {
                        description: t("colorAreaPage.propRadius"),
                        type: "number",
                        name: "radius"
                    },
                    {
                        description: t("colorAreaPage.propIsFlush"),
                        defaultValue: "false",
                        name: "isFlush",
                        type: "boolean"
                    },
                    {
                        description: t("colorAreaPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("colorAreaPage.propTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("colorAreaPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("colorAreaPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ColorAreaPage;
